import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { HttpErrorInterceptor } from './core/interceptor/http-error.interceptor';
import { JwtInterceptor } from './core/interceptor/jwt.interceptor';
import { environment } from '@environment';
import { LayoutModule } from './layout/layout.module';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { IbpaasBaseComponentModule } from './base/ibpaas-base-component.module';
import { ExampleModule } from './base/example/example.module';
import { MultiTranslateHttpLoader } from './base/loader/multi-translate-http-loader';

// Azure AD 設定參考 https://docs.microsoft.com/zh-tw/azure/active-directory/develop/tutorial-v2-angular-auth-code
// Azure AD 會用到判斷是不是 IE 11
const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

// 多國語系設定參考 https://edwardzou.blogspot.com/2019/01/ngx-translate.html
// AoT requires an exported function for factories
// 建立 TranslateHttpLoader 作為語系檔的讀取器
// 有兩個參數可以使用，分別是多語系的檔案路徑及副檔名，如果都沒有設定的話預設會使用「/assets/i18n/」及「.json」，
// export function HttpLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http);
// }

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    IbpaasBaseComponentModule,
    ExampleModule,
    SharedModule,
    // Initiate the MSAL library with the MSAL configuration object
    MsalModule.forRoot(
      // msalInstance
      new PublicClientApplication({
        auth: {
          clientId: environment.clientId,
          authority: environment.authority,
          redirectUri: environment.redirectUrl
        },
        cache: {
          // cacheLocation: 'localStorage', // 這裡跟 Microsoft 教學範例不一樣是因為不要保留登入狀態
          storeAuthStateInCookie: isIE, // Set to true for Internet Explorer 11
        },
        system: {
          loggerOptions: {
            loggerCallback: (logLevel, message, containsPii) => {
              console.log(message);
            },
            logLevel: LogLevel.Info,
            piiLoggingEnabled: false,
          },
        },
      }),
      // // The routing guard configuration.
      // 提供 MsalGuard 類別，可讓您在存取受保護的路由之前，用來保護路由和要求驗證
      {
        interactionType: InteractionType.Redirect, // 提示使用者重新導向進行驗證
        authRequest: {
          scopes: ['user.read'],  // 跟 Azure AD 裡設定的 API Permissions 一致
        },
      },
      // MSAL interceptor configuration.
      // The protected resource mapping maps your web API with the corresponding app scopes.
      // If your code needs to call another web API, add the URI mapping here.
      // MSAL Angular 會提供 Interceptor 類別給已知受保護的資源，而該類別可為使用 Angular http client 的傳出要求自動取得 tokens。
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map([
            ['https://graph.microsoft.com/graph/v1.0/me', ['user.read']]
        ])
      }
    ),
    HttpClientModule,
    // 將 TranslateHttpLoader 作為 TranslateModule 的語系檔讀取器 ( loader )
    TranslateModule.forRoot({
      defaultLanguage: 'en-us',
      loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient): MultiTranslateHttpLoader => {
            return new MultiTranslateHttpLoader(http, {
              resources: [
                { prefix: './assets/i18n/base/', suffix: '.json' },
                { prefix: './assets/i18n/', suffix: '.json' },
              ]
            });
          },
          deps: [HttpClient]
      }
    }),
    LayoutModule
  ],
  providers: [
    {
      // 添加攔截器攔截所有 HTTP Request，會添加 access token，以便可以訪問受保護的資源，像是 Graph API
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalGuard,  // MsalGuard added as provider here
    MessageService,  // 配合全域的 toast message service (SystemMessageService) 只能在這邊設 providers,
    ConfirmationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    DialogService,
    DynamicDialogRef,
    DynamicDialogConfig
  ],
  bootstrap: [
    AppComponent,
    MsalRedirectComponent // MsalRedirectComponent bootstrapped here 這是會處理重新導向的專用重新導向元件
  ]
})
export class AppModule { }
